/**
 * Pure assembler for the wizard's Step 7 (D1.5.8) commit step.
 *
 * The per-step draft fragments persisted by Steps 1–6 are partial,
 * liberal shapes optimized for editing (enabled-per-day rows, raw
 * strings, optional fields). The D1.6.1 commit service expects a
 * strict `TenantProvisioningSpec` with all required slots populated.
 *
 * `assembleProvisioningSpec(payload)` is that translation. It returns
 * a discriminated result:
 *   • `{ ok: true;  spec }`   — ready to POST to /api/platform/tenants/commit
 *   • `{ ok: false; issues }` — enumerated list of missing/invalid fields
 *
 * Zod parsing happens server-side at commit time — the assembler's job
 * is to collect the shape the server Zod will accept (or surface the
 * admin-visible issues pre-network).
 */
import { customerSubdomain, adminSubdomain } from '../pages/platform/_components/StepDomain.js';
import {
  readBrandKitDraft,
  type BrandKitDraft,
} from './brand-kit-form.js';
import {
  DAY_KEYS,
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
  DEFAULT_TIMEZONE,
  readTenantConfigDraft,
  type DayKey,
  type TenantConfigDraft,
} from './tenant-config-form.js';
import {
  readOwnerDraft,
  type OwnerDraft,
} from './tenant-owner-form.js';
import {
  readModulesDraft,
  type ModulesDraft,
} from './tenant-modules-form.js';
import { validateTenantSlug } from './tenant-slug.js';

// ─── Spec shape (mirrors packages/core/tenant/provisioning.ts) ───────────────

export type ProvisioningSpecBasic = {
  slug: string;
  legalName: string;
  displayName: string;
  vertical: string;
};

export type ProvisioningSpecDomain = {
  kind: 'subdomain' | 'cname_customer' | 'cname_admin';
  hostname: string;
  surface: 'customer' | 'admin';
};

export type ProvisioningSpecBrandKit = {
  version: 1;
  logo: {
    primarySvg: string;
    primaryPng: string;
    favicon: string;
    emailHeader: string;
  };
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
  typography: {
    primaryFontFamily: string;
    headingFontFamily: string;
    fontUrls: ReadonlyArray<unknown>;
  };
  copy: {
    companyName: string;
    supportEmail: string;
  };
  terminology: Record<string, string>;
};

export type ProvisioningSpecBusinessHours = {
  schedule: Partial<Record<DayKey, ReadonlyArray<{ open: string; close: string }>>>;
  holidays: ReadonlyArray<unknown>;
};

export type ProvisioningSpecBusinessMeta = {
  legalEntityName: string;
  entityType: string;
  einTaxId?: string;
  primaryPhone: string;
  primaryAddress: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
};

export type ProvisioningSpecServiceEntry = {
  slug: string;
  displayName: string;
  priceCents: number;
  durationMinutes: number;
  applicableStaffRoles: ReadonlyArray<string>;
  bookingRules: {
    minLeadTimeMinutes: number;
    maxAdvanceDays: number;
    allowMultiDay: boolean;
    requiresVaccinationCheck: boolean;
  };
  active: boolean;
};

export type ProvisioningSpecConfig = {
  timezone: string;
  currency: string;
  locale: string;
  businessHours: ProvisioningSpecBusinessHours;
  serviceCatalog: ReadonlyArray<ProvisioningSpecServiceEntry>;
  businessMeta: ProvisioningSpecBusinessMeta;
  defaultRoles: ReadonlyArray<unknown>;
};

export type ProvisioningSpecOwner = {
  email: string;
  firstName: string;
  lastName: string;
  roleSlug: 'owner';
};

export type ProvisioningSpec = {
  basic: ProvisioningSpecBasic;
  domains: ReadonlyArray<ProvisioningSpecDomain>;
  branding: ProvisioningSpecBrandKit;
  config: ProvisioningSpecConfig;
  modules: { requestedSlugs: ReadonlyArray<string> };
  owner: ProvisioningSpecOwner;
};

// ─── Issue shape ─────────────────────────────────────────────────────────────

/**
 * Stable step ids the review screen uses for chip navigation. Matches
 * `TENANT_WIZARD_STEPS` in `lib/tenant-draft.ts`.
 */
export type AssemblyStep =
  | 'basic-info'
  | 'domain'
  | 'brand'
  | 'config'
  | 'modules'
  | 'admin-user';

export type AssemblyIssue = {
  step: AssemblyStep;
  path: string;
  message: string;
};

export type AssemblyResult =
  | { ok: true; spec: ProvisioningSpec }
  | { ok: false; issues: ReadonlyArray<AssemblyIssue> };

// ─── Entry point ─────────────────────────────────────────────────────────────

export function assembleProvisioningSpec(
  payload: Record<string, unknown>,
): AssemblyResult {
  const issues: AssemblyIssue[] = [];

  const basic = assembleBasic(payload, issues);
  const domains = basic ? assembleDomains(basic.slug) : [];
  const branding = assembleBranding(readBrandKitDraft(payload), issues);
  const config = assembleConfig(readTenantConfigDraft(payload), issues);
  const modules = assembleModules(readModulesDraft(payload));
  const owner = assembleOwner(readOwnerDraft(payload), issues);

  if (!basic || !branding || !config || !owner) {
    return { ok: false, issues };
  }

  return {
    ok: true,
    spec: {
      basic,
      domains,
      branding,
      config,
      modules,
      owner,
    },
  };
}

// ─── Section assemblers ──────────────────────────────────────────────────────

function assembleBasic(
  payload: Record<string, unknown>,
  issues: AssemblyIssue[],
): ProvisioningSpecBasic | null {
  const legalName = readString(payload.legalName)?.trim();
  const displayName = readString(payload.displayName)?.trim();
  const slug = readString(payload.slug)?.trim();
  const vertical = readString(payload.vertical) ?? 'pet-services';

  let ok = true;

  if (!legalName) {
    issues.push({ step: 'basic-info', path: 'legalName', message: 'Legal name is required.' });
    ok = false;
  }
  if (!displayName) {
    issues.push({ step: 'basic-info', path: 'displayName', message: 'Display name is required.' });
    ok = false;
  }
  if (!slug) {
    issues.push({ step: 'basic-info', path: 'slug', message: 'Slug is required.' });
    ok = false;
  } else {
    const slugValidation = validateTenantSlug(slug);
    if (!slugValidation.ok) {
      issues.push({
        step: 'basic-info',
        path: 'slug',
        message: 'Slug has the wrong shape (3–40 lowercase letters, digits, hyphens).',
      });
      ok = false;
    }
  }

  if (!ok) return null;
  return {
    slug: slug!,
    legalName: legalName!,
    displayName: displayName!,
    vertical,
  };
}

function assembleDomains(slug: string): ReadonlyArray<ProvisioningSpecDomain> {
  // Per design §6: v1 auto-provisions two subdomains. Custom CNAMEs are
  // a later workstream (Phase 2). Step 2's UI only previews these — the
  // draft doesn't persist them, so the assembler derives them from slug.
  return [
    { kind: 'subdomain', hostname: customerSubdomain(slug), surface: 'customer' },
    { kind: 'subdomain', hostname: adminSubdomain(slug), surface: 'admin' },
  ];
}

function assembleBranding(
  draft: BrandKitDraft,
  issues: AssemblyIssue[],
): ProvisioningSpecBrandKit | null {
  const logo = draft.logo ?? {};
  const colors = draft.colors ?? {};
  const typography = draft.typography ?? {};
  const copy = draft.copy ?? {};

  let ok = true;

  const required: ReadonlyArray<{ key: keyof typeof logo; label: string }> = [
    { key: 'primarySvg', label: 'primary SVG logo' },
    { key: 'primaryPng', label: 'primary PNG logo' },
    { key: 'favicon', label: 'favicon' },
    { key: 'emailHeader', label: 'email header' },
  ];
  for (const { key, label } of required) {
    if (!logo[key] || (logo[key] as string).length === 0) {
      issues.push({
        step: 'brand',
        path: `logo.${key}`,
        message: `Brand kit is missing the ${label} upload.`,
      });
      ok = false;
    }
  }

  const requiredColors: ReadonlyArray<keyof typeof colors> = [
    'primary',
    'secondary',
    'accent',
    'background',
    'text',
  ];
  for (const key of requiredColors) {
    const value = colors[key];
    if (!value || !/^#[0-9a-fA-F]{6}$/.test(value)) {
      issues.push({
        step: 'brand',
        path: `colors.${String(key)}`,
        message: `Color “${String(key)}” must be a 6-digit hex (e.g. #62A2C3).`,
      });
      ok = false;
    }
  }

  if (!typography.primaryFontFamily) {
    issues.push({ step: 'brand', path: 'typography.primaryFontFamily', message: 'Primary font family is required.' });
    ok = false;
  }
  if (!typography.headingFontFamily) {
    issues.push({ step: 'brand', path: 'typography.headingFontFamily', message: 'Heading font family is required.' });
    ok = false;
  }

  if (!copy.companyName) {
    issues.push({ step: 'brand', path: 'copy.companyName', message: 'Company name is required.' });
    ok = false;
  }
  if (!copy.supportEmail) {
    issues.push({ step: 'brand', path: 'copy.supportEmail', message: 'Support email is required.' });
    ok = false;
  }

  if (!ok) return null;
  return {
    version: 1,
    logo: {
      primarySvg: logo.primarySvg!,
      primaryPng: logo.primaryPng!,
      favicon: logo.favicon!,
      emailHeader: logo.emailHeader!,
    },
    colors: {
      primary: colors.primary!,
      secondary: colors.secondary!,
      accent: colors.accent!,
      background: colors.background!,
      text: colors.text!,
    },
    typography: {
      primaryFontFamily: typography.primaryFontFamily!,
      headingFontFamily: typography.headingFontFamily!,
      fontUrls: [],
    },
    copy: {
      companyName: copy.companyName!,
      supportEmail: copy.supportEmail!,
    },
    terminology: {},
  };
}

function assembleConfig(
  draft: TenantConfigDraft,
  issues: AssemblyIssue[],
): ProvisioningSpecConfig | null {
  let ok = true;

  const schedule: Partial<Record<DayKey, ReadonlyArray<{ open: string; close: string }>>> = {};
  for (const day of DAY_KEYS) {
    const row = draft.businessHours?.[day];
    if (!row) continue;
    if (!row.enabled) continue;
    if (!row.open || !row.close) {
      issues.push({
        step: 'config',
        path: `businessHours.${day}`,
        message: 'Enabled day is missing open or close time.',
      });
      ok = false;
      continue;
    }
    schedule[day] = [{ open: row.open, close: row.close }];
  }

  const serviceCatalog: ProvisioningSpecServiceEntry[] = (draft.serviceCatalog ?? []).map(
    (s) => ({
      slug: s.slug,
      displayName: s.displayName,
      priceCents: s.priceCents,
      durationMinutes: s.durationMinutes,
      applicableStaffRoles: [],
      bookingRules: {
        minLeadTimeMinutes: 0,
        maxAdvanceDays: 90,
        allowMultiDay: false,
        requiresVaccinationCheck: true,
      },
      active: s.active,
    }),
  );
  if (serviceCatalog.length === 0) {
    issues.push({
      step: 'config',
      path: 'serviceCatalog',
      message: 'At least one service is required.',
    });
    ok = false;
  }

  const meta = draft.businessMeta;
  if (!meta?.legalEntityName) {
    issues.push({ step: 'config', path: 'businessMeta.legalEntityName', message: 'Legal entity name is required.' });
    ok = false;
  }
  if (!meta?.entityType) {
    issues.push({ step: 'config', path: 'businessMeta.entityType', message: 'Entity type is required.' });
    ok = false;
  }
  if (!meta?.primaryPhone) {
    issues.push({ step: 'config', path: 'businessMeta.primaryPhone', message: 'Primary phone is required.' });
    ok = false;
  }
  const address = meta?.primaryAddress;
  const requiredAddress: ReadonlyArray<'line1' | 'city' | 'state' | 'postalCode'> = [
    'line1',
    'city',
    'state',
    'postalCode',
  ];
  for (const key of requiredAddress) {
    if (!address?.[key]) {
      issues.push({
        step: 'config',
        path: `businessMeta.primaryAddress.${key}`,
        message: `Address ${key} is required.`,
      });
      ok = false;
    }
  }

  if (!ok) return null;
  return {
    timezone: draft.timezone ?? DEFAULT_TIMEZONE,
    currency: draft.currency ?? DEFAULT_CURRENCY,
    locale: draft.locale ?? DEFAULT_LOCALE,
    businessHours: { schedule, holidays: [] },
    serviceCatalog,
    businessMeta: {
      legalEntityName: meta!.legalEntityName!,
      entityType: meta!.entityType!,
      einTaxId: meta!.einTaxId || undefined,
      primaryPhone: meta!.primaryPhone!,
      primaryAddress: {
        line1: address!.line1!,
        line2: address!.line2 || undefined,
        city: address!.city!,
        state: address!.state!,
        postalCode: address!.postalCode!,
        country: address!.country ?? 'US',
      },
    },
    defaultRoles: [],
  };
}

function assembleModules(draft: ModulesDraft): { requestedSlugs: ReadonlyArray<string> } {
  return { requestedSlugs: draft.requested };
}

function assembleOwner(
  draft: OwnerDraft,
  issues: AssemblyIssue[],
): ProvisioningSpecOwner | null {
  let ok = true;

  const email = (draft.email ?? '').trim();
  const firstName = (draft.firstName ?? '').trim();
  const lastName = (draft.lastName ?? '').trim();

  if (!email) {
    issues.push({ step: 'admin-user', path: 'owner.email', message: 'Owner email is required.' });
    ok = false;
  } else if (!email.includes('@')) {
    issues.push({
      step: 'admin-user',
      path: 'owner.email',
      message: 'Owner email looks malformed.',
    });
    ok = false;
  }
  if (!firstName) {
    issues.push({ step: 'admin-user', path: 'owner.firstName', message: 'Owner first name is required.' });
    ok = false;
  }
  if (!lastName) {
    issues.push({ step: 'admin-user', path: 'owner.lastName', message: 'Owner last name is required.' });
    ok = false;
  }

  // Invite mode (magic_link vs temp_password) lives in the owner-invite
  // flow (D1.7.1); the commit service takes only the identity block.

  if (!ok) return null;
  return {
    email,
    firstName,
    lastName,
    roleSlug: 'owner',
  };
}

// ─── Read helpers ────────────────────────────────────────────────────────────

function readString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}
